import { BrowserModule } from '@angular/platform-browser';
import { NgModule, Injectable, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ColorPickerModule } from 'ngx-color-picker';
import { environment } from '../environments/environment';

import { ChartsModule } from 'ng2-charts';
import { NgxGaugeModule } from 'ngx-gauge';

import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { MaterialDesignModule } from './material-design/material-design.module';
import { AppComponent } from './app.component';
import { HomeComponent, HomeLandingComponent } from './public/home.component';
import { ConfirmRegistrationComponent } from './public/registration/registration-confirmation.component';
import { LoginComponent } from './public/auth/login.component';
import { VerifyMfaComponent } from './public/auth/verify-mfa.component';
import { ForgotPasswordComponent, ConfirmForgotPasswordComponent } from './public/auth/forgot-password.component';
import { NewPasswordComponent } from './public/auth/new-password.component';
import { SecureHomeComponent, SecureHomeDefaultRouterComponent, WorkingAccountSelectorComponent } from './secured/secure-home.component';

import { SessionService } from './services/session.service';
import { CognitoService } from './services/cognito.service';
import { RouterAuthorizerService } from './services/router-authorizer.service';
import { PlatformApiService } from './services/platform-api.service';
import { LayoutModule } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { SurveyComponent } from './secured/survey/survey.component';
import { RiskmapComponent } from './secured/visualizations/riskmap/riskmap.component';
import { CategoryRiskComponent } from './secured/visualizations/category-risk/category-risk.component';
import { MspRiskmapComponent } from './secured/visualizations/msp-riskmap/msp-riskmap.component';
import { MspCompanyRiskComponent } from './secured/visualizations/msp-company-risk/msp-company-risk.component';

import { AuthManagerComponent } from './public/auth/auth-manager.component';
import { MspDashboardComponent } from './secured/dashboards/msp-dashboard.component';
import { AccountListComponent } from './secured/admin/account-list.component';
import { AdminAssessmentListComponent } from './secured/admin/assessment-list.component';
import { AdminAssessmentEditComponent } from './secured/admin/assessment-edit.component';
import { PlanListComponent } from './secured/admin/plan-list.component';
import { PlanEditComponent, YesNoCancelPromptComponent } from './secured/admin/plan-edit.component';
import { AssessmentWorkComponent } from './secured/assessment/assessment-work.component';
import { AssessmentService } from './services/assessment.service';
import { RiskSelectorComponent } from './secured/visualizations/risk-selector/risk-selector.component';
import { SectionRadarComponent } from './secured/visualizations/section-radar/section-radar.component';
import { NoNavLayoutComponent } from './secured/layouts/no-nav-layout/no-nav-layout.component';
import { SideNavLayoutComponent } from './secured/layouts/side-nav-layout/side-nav-layout.component';
import { SubSectionDetailComponent } from './secured/visualizations/subsection-detail/subsection-detail.component';
import { ScoreHistoryGraphComponent } from './secured/visualizations/score-history-graph/score-history-graph.component';
import { AdminAccountDashboardComponent } from './secured/dashboards/admin-account-dashboard.component';
import { ScoreHistogramComponentComponent } from './secured/visualizations/score-histogram-component/score-histogram-component.component';
import { AssessmentStatusByQuarterComponent } from './secured/visualizations/assessment-status-by-quarter/assessment-status-by-quarter.component';
import { AccountRiskByQuarterComponent } from './secured/visualizations/account-risk-by-quarter/account-risk-by-quarter.component';
import { CompanyAssessmentDashboardComponent } from './secured/dashboards/company-assessment-dashboard.component';
import { CompanyCertificationDashboardComponent } from './secured/dashboards/company-certification-dashboard.component';
import { AssessmentReportComponent } from './secured/assessment/assessment-report.component';
import { AssessmentTemplateDashboardComponent } from './secured/dashboards/assessment-template-dashboard.component';
import { PlanDashboardComponent } from './secured/dashboards/plan-dashboard.component';
import { IndustryComparisonDashboardComponent } from './secured/dashboards/industry-comparison-dashboard.component';
import { PercentileGraphComponentComponent } from './secured/visualizations/percentile-graph-component/percentile-graph-component.component';
import { InfoTileComponent } from './secured/visualizations/info-tile/info-tile.component';
import { AssessmentListComponent, AskKeepAnswersPromptComponent  } from './secured/assessment/assessment-list.component';
import { GridSpinnerComponent } from './shared/grid-spinner.component';
import { ConnectWiseLayoutComponent } from './secured/layouts/connectwise-layout/connectwise-layout.component';
import { ConnectwiseProvisioningComponent } from './partner/connectwise-provisioning.component';
import { ConnectwiseHandlerComponent } from './partner/connectwise-handler.component';
import { SbamLayoutComponent } from './secured/layouts/sbam-layout/sbam-layout.component';
import { ApiHttpInterceptor } from './api-http-interceptor';
import { IntegrationPartnerListComponent } from './secured/admin/integration-partner-list.component';
import { IntegrationPartnerEditComponent } from './secured/admin/integration-partner-edit.component';
import { PartnerDashboardComponent } from './secured/dashboards/partner-dashboard.component';
import { AccountApiKeysComponent, ShowNewApiKeyComponent } from './secured/settings-wizard/account-api-keys.component';
import { BrandListComponent } from './secured/admin/brand-list.component';
import { BrandDashboardComponent } from './secured/dashboards/brand-dashboard.component';
import { AccountBrandComponent } from './secured/admin/account-brand.component';
import { DialogPromptComponent } from './shared/dialog-prompt.component';
import { BuyNowPromptComponent } from './shared/buynow-prompt.component';
import { SatPopoverModule } from '@ncstate/sat-popover';
import { ExpiredComponent } from './secured/expired/expired.component';
import { TocComponent } from './shared/toc.component';
import { CustomScrollDirective } from './shared/custom-scroll.directive';
import { SupportTicketComponent } from './shared/support.component';
import { UnavailableDashboardComponent } from './secured/expired/unavailable-dashboard.component';
import { BillingContactComponent } from './shared/billing-contact.component';
import { ConfirmationComponent } from './shared/confirmation.component';
import { OidcConnectWiseService, OidcSbamService } from './services/oidc.service';
import { DialogComponent } from './shared/dialog.component';
import { ProfileInfoComponent } from './secured/settings-wizard/profile-info.component';
import { ProfileCredentialsComponent } from './secured/settings-wizard/profile-credentials.component';
import { ProfileLinkedAccountsComponent } from './secured/settings-wizard/profile-linked-accounts.component';
import { DataTableColumnComponent, DataTableColumnActionComponent, DataTableComponent } from './shared/tile-data-table/tile-data-table.component';
import { WizardComponent, WizardMenuItemComponent, WizardMenuDividerComponent, WizardMenuSubmenuComponent } from './shared/wizard.component';
import { SettingsWizardComponent } from './secured/settings-wizard/settings-wizard-dialog.component';
import { SubAccountDashboardComponent } from './secured/dashboards/sub-account-dashboard.component';
import { SubAccountSummaryListComponent } from './secured/account/sub-account-summary-list.component';
import { SsoMatchComponent } from './public/auth/sso-match.component';

import * as Sentry from '@sentry/browser';
import { AccountAssessmentListComponent} from './secured/assessment/account-assessment-list.component';
import { AssessmentTypeListComponent } from './secured/assessment/assessment-type-list.component';
import { CertificationAssessmentTypeListComponent } from './secured/assessment/certification-assessment-type-list.component';
import { AccountSettingsComponent } from './secured/settings-wizard/account-settings.component';
import { AccountUsersComponent } from './secured/settings-wizard/account-users.component';
import { AccountReportBrandingComponent } from './secured/settings-wizard/account-report-branding.component';
import { NotificationsComponent, NotificationComponent } from './secured/account/notifications.component';
import { NotificationListComponent } from './secured/admin/notification-list.component';
import { NotificationDashboardComponent } from './secured/dashboards/notification-dashboard.component';
import { NotificationEditComponent } from './secured/admin/notification-edit.component';

import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { UserIdleModule } from 'angular-user-idle';
import { EmailTemplateListComponent } from './secured/admin/email-template-list.component';
import { EmailTemplateDashboardComponent } from './secured/dashboards/email-template-dashboard.component';
import { EmailTemplateEditComponent } from './secured/admin/email-template-edit.component';
import { ProfilePreferencesComponent } from './secured/settings-wizard/profile-preferences.component';
import { AccountManageIntegrationComponent } from './secured/settings-wizard/account-manage-integration.component';
import { AccountManageUsersComponent } from './secured/settings-wizard/account-manage-users.component';
import { AccountManageCompaniesComponent } from './secured/settings-wizard/account-manage-companies.component';
import { AutoCompleteInfiniteScrollDirective } from './shared/infinite-scroll-select.component';
import { MatSelectInfiniteScrollModule } from 'ng-mat-select-infinite-scroll';
import { SplashScreenComponent } from './secured/splash-screen/splash-screen.component';
import { ProfileMfaComponent } from './secured/settings-wizard/profile-mfa.component';
import { ManageCompanyDropdownComponent } from './shared/manage-company-dropdown';
import { AutomateClientDropdownComponent } from './shared/automate-client-dropdown';
import { AccountDropdownComponent } from './shared/account-dropdown';
import { AssessmentDropdownComponent } from './shared/assessments-dropdown';
import { CSPartnerDropdownComponent } from './shared/cs-partner-dropdown';

import { QRCodeModule } from 'angular2-qrcode';
import { AccountAutomateIntegrationComponent } from './secured/settings-wizard/account-automate-integration.component';
import { DeviceVulnerabilityScanDashboardComponent } from './secured/dashboards/device-vulnerability-scan-dashboard.component';
import { ManageUserDropdownComponent } from './shared/manage-user-dropdown';
import { UserListComponent } from './secured/admin/user-list.component';
import { AdminUserDashboardComponent } from './secured/dashboards/admin-user-dashboard.component';
import { AccountSubAccountsComponent } from './secured/settings-wizard/account-sub-accounts.component';
import { AutomateScanTargetListComponent } from './secured/scanner/automate-scan-target-list.component';
import { ScanConfigurationListComponent } from './secured/admin/scan-configuration-list.component';
import { ScanConfigurationEditComponent } from './secured/admin/scan-configuration-edit.component';
import { ScanConfigurationDashboardComponent } from './secured/admin/scan-configuration-dashboard.component';
import { ScanResultsListComponent } from './secured/scanner/scan-results-list.component';
import { ScanResultXccdfSummaryComponent } from './secured/scanner/scan-result-xccdf-summary.component';
import { ScanResultOvalSummaryComponent } from './secured/scanner/scan-result-oval-summary.component';
import { AccountCertificationListComponent } from './secured/assessment/account-certification-list.component';
import { CertificationListComponent } from './secured/admin/certification-list.component';
import { CertificationDashboardComponent } from './secured/dashboards/certification-dashboard.component';

Sentry.init({
  dsn: environment.sentry.dsn,
  environment: environment.sentry.environment
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}
  handleError(error) {
    console.error(error);
//    Sentry.captureException(error.originalError || error);
  }
}

export function jwtOptionsFactory(cognito) {
  return {
    tokenGetter: () => {
      return cognito.getIdToken();
    },
    whitelistedDomains: environment.securedDomains
  }
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    HomeLandingComponent,
    LoginComponent,
    VerifyMfaComponent,
    NewPasswordComponent,
    ForgotPasswordComponent,
    ConfirmForgotPasswordComponent,
    SecureHomeComponent,
    SecureHomeDefaultRouterComponent,
    WorkingAccountSelectorComponent,
    ConfirmRegistrationComponent,
    SurveyComponent,
    RiskmapComponent,
    CategoryRiskComponent,
    MspRiskmapComponent,
    MspCompanyRiskComponent,
    AuthManagerComponent,
    MspDashboardComponent,
    AccountListComponent,
    AdminAssessmentListComponent,
    AdminAssessmentEditComponent,
    PlanListComponent,
    PlanEditComponent,
    AssessmentWorkComponent,
    RiskSelectorComponent,
    SectionRadarComponent,
    NoNavLayoutComponent,
    SideNavLayoutComponent,
    ConnectWiseLayoutComponent,
    SbamLayoutComponent,
    SubSectionDetailComponent,
    ScoreHistoryGraphComponent,
    AdminAccountDashboardComponent,
    ScoreHistogramComponentComponent,
    AssessmentStatusByQuarterComponent,
    AccountRiskByQuarterComponent,
    CompanyAssessmentDashboardComponent,
    CompanyCertificationDashboardComponent,
    AssessmentReportComponent,
    AssessmentTemplateDashboardComponent,
    PlanDashboardComponent,
    IndustryComparisonDashboardComponent,
    PercentileGraphComponentComponent,
    InfoTileComponent,
    AssessmentListComponent,
    AskKeepAnswersPromptComponent,
    YesNoCancelPromptComponent,
    GridSpinnerComponent,
    ConnectwiseProvisioningComponent,
    ConnectwiseHandlerComponent,
    IntegrationPartnerListComponent,
    IntegrationPartnerEditComponent,
    PartnerDashboardComponent,
    AccountApiKeysComponent,
    ShowNewApiKeyComponent,
    BrandListComponent,
    BrandDashboardComponent,
    AccountBrandComponent,
    DialogPromptComponent,
    BuyNowPromptComponent,
    ExpiredComponent,
    TocComponent,
    CustomScrollDirective,
    SupportTicketComponent,
    UnavailableDashboardComponent,
    BillingContactComponent,
    ConfirmationComponent,
    DialogComponent,
    ProfileInfoComponent,
    ProfileCredentialsComponent,
    ProfileLinkedAccountsComponent,
    AccountSettingsComponent,
    AccountUsersComponent,
    AccountReportBrandingComponent,
    AccountManageIntegrationComponent,
    AccountManageUsersComponent,
    AccountManageCompaniesComponent,
    DataTableComponent,
    DataTableColumnComponent,
    DataTableColumnActionComponent,
    WizardComponent,
    WizardMenuItemComponent,
    WizardMenuDividerComponent,
    WizardMenuSubmenuComponent,
    SettingsWizardComponent,
    SubAccountDashboardComponent,
    SubAccountSummaryListComponent,
    SsoMatchComponent,
    AccountAssessmentListComponent,
    AccountCertificationListComponent,
    NotificationsComponent,
    NotificationComponent,
    NotificationListComponent,
    NotificationDashboardComponent,
    NotificationEditComponent,
    EmailTemplateListComponent,
    EmailTemplateDashboardComponent,
    EmailTemplateEditComponent,
    ProfilePreferencesComponent,
    AutoCompleteInfiniteScrollDirective,
    ManageCompanyDropdownComponent,
    CSPartnerDropdownComponent,
    ManageUserDropdownComponent,
    AutomateClientDropdownComponent,
    SplashScreenComponent,
    AccountApiKeysComponent,
    ProfileMfaComponent,
    AccountAutomateIntegrationComponent,
    DeviceVulnerabilityScanDashboardComponent,
    UserListComponent,
    AdminUserDashboardComponent,
    AccountSubAccountsComponent,
    AutomateScanTargetListComponent,
    ScanConfigurationListComponent,
    ScanConfigurationEditComponent,
    ScanConfigurationDashboardComponent,
    ScanResultsListComponent,
    ScanResultXccdfSummaryComponent,
    ScanResultOvalSummaryComponent,
    AssessmentTypeListComponent,
    CertificationListComponent,
    CertificationDashboardComponent,
    AccountDropdownComponent,
    CertificationAssessmentTypeListComponent,
    AssessmentDropdownComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    ColorPickerModule,
    MaterialDesignModule,
    AppRoutingModule,
    LayoutModule,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    ChartsModule,
    HttpClientModule,
    // JwtModule.forRoot({
    //   jwtOptionsProvider: {
    //     provide: JWT_OPTIONS,
    //     useFactory: jwtOptionsFactory,
    //     deps: [CognitoService]
    //   }
    // })
    SatPopoverModule,
    NgxGaugeModule,
    CKEditorModule,
    UserIdleModule.forRoot({idle: 15*60, timeout: 30, ping: 120}),
    MatSelectInfiniteScrollModule,
    QRCodeModule
  ],
  entryComponents: [
    AskKeepAnswersPromptComponent,
    YesNoCancelPromptComponent,
    ShowNewApiKeyComponent,
    BuyNowPromptComponent,
    TocComponent,
    SupportTicketComponent,
    BillingContactComponent,
    ConfirmationComponent,
    SettingsWizardComponent,
    NotificationComponent,
    SplashScreenComponent,
    AssessmentTypeListComponent,
    CertificationAssessmentTypeListComponent,
  ],
  providers: [
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    RouterAuthorizerService,
    SessionService,
    CognitoService,
    PlatformApiService,
    AssessmentService,
    OidcConnectWiseService,
    OidcSbamService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiHttpInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() { }
}
